import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Socket } from 'socket.io-client';
import { useAppContext } from '../../contexts/auth';
import useGetGeneralAppData from '../../hooks/useGetGeneralAppData';
import { PRESENCE_EVENTS } from './consult-presence.constants';
import {
  connectConsultPresenceSocket,
  disconnectConsultPresenceSocket,
  getConsultPresenceSocket,
} from './consult-presence.socket';
import {
  ConsultPresenceErrorEvent,
  ConsultPresenceMap,
  ConsultPresenceSnapshot,
  ConsultPresenceUpdatedEvent,
  ConsultPresenceViewer,
} from './consult-presence.types';
import { formatPresenceBadge, getConsultViewers } from './consult-presence.utils';

interface ConsultPresenceContextValue {
  consults: ConsultPresenceMap;
  isConnected: boolean;
  isEnabled: boolean;
  currentUserId: string;
  getViewersForConsult: (consultCode: number | string) => ConsultPresenceViewer[];
  getBadgeText: (consultCode: number | string) => string | null;
  socket: Socket | null;
}

const ConsultPresenceContext = createContext({} as ConsultPresenceContextValue);

export function useConsultPresence(): ConsultPresenceContextValue {
  return useContext(ConsultPresenceContext);
}

interface ConsultPresenceProviderProps {
  children: ReactNode;
}

export function ConsultPresenceProvider({ children }: ConsultPresenceProviderProps) {
  const { signed } = useAppContext();
  const { userCode, isCustomer } = useGetGeneralAppData();
  const [consults, setConsults] = useState<ConsultPresenceMap>({});
  const [isConnected, setIsConnected] = useState(false);

  const isEnabled = signed && !isCustomer;
  const currentUserId = String(userCode);

  useEffect(() => {
    if (!isEnabled) {
      disconnectConsultPresenceSocket();
      setConsults({});
      setIsConnected(false);
      return undefined;
    }

    const socket = connectConsultPresenceSocket();
    if (!socket) return undefined;

    const emitWatch = () => {
      socket.emit(PRESENCE_EVENTS.WATCH);
    };

    const handleConnect = () => {
      setIsConnected(true);
      emitWatch();
    };

    const handleDisconnect = () => {
      setIsConnected(false);
    };

    const handleSnapshot = (payload: ConsultPresenceSnapshot) => {
      setConsults(payload.consults ?? {});
    };

    const handleUpdated = (payload: ConsultPresenceUpdatedEvent) => {
      const key = String(payload.consultCode);
      setConsults((prev) => {
        if (!payload.viewers?.length) {
          const next = { ...prev };
          delete next[key];
          return next;
        }
        return { ...prev, [key]: payload.viewers };
      });
    };

    const handleError = (payload: ConsultPresenceErrorEvent) => {
      console.error('[consult-presence]', payload.message);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.io.on('reconnect', emitWatch);
    socket.on(PRESENCE_EVENTS.SNAPSHOT, handleSnapshot);
    socket.on(PRESENCE_EVENTS.UPDATED, handleUpdated);
    socket.on(PRESENCE_EVENTS.ERROR, handleError);

    if (socket.connected) {
      handleConnect();
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.io.off('reconnect', emitWatch);
      socket.off(PRESENCE_EVENTS.SNAPSHOT, handleSnapshot);
      socket.off(PRESENCE_EVENTS.UPDATED, handleUpdated);
      socket.off(PRESENCE_EVENTS.ERROR, handleError);
    };
  }, [isEnabled]);

  const getViewersForConsult = useCallback(
    (consultCode: number | string) => getConsultViewers(consults, consultCode),
    [consults],
  );

  const getBadgeText = useCallback(
    (consultCode: number | string) => formatPresenceBadge(
      getConsultViewers(consults, consultCode),
      currentUserId,
    ),
    [consults, currentUserId],
  );

  const value = useMemo((): ConsultPresenceContextValue => ({
    consults,
    isConnected,
    isEnabled,
    currentUserId,
    getViewersForConsult,
    getBadgeText,
    socket: isEnabled ? getConsultPresenceSocket() : null,
  }), [consults, isConnected, isEnabled, currentUserId, getViewersForConsult, getBadgeText]);

  return (
    <ConsultPresenceContext.Provider value={value}>
      {children}
    </ConsultPresenceContext.Provider>
  );
}
